import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import { Props } from '@/forms/NoteForm/types';
import useNoteTypes from '@/hooks/useNoteTypes';
import useTimeout from '@/hooks/useTimeout';
import NoteRepository from '@/repositories/NoteRepository';
import { NoteFormData, NotePayload } from '@/types/note';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import Heading from '@/ui/components/Heading/Heading';
import Spinner from '@/ui/components/Spinner';
import { minutesBetween } from '@/utils/dates';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import FieldError from '../FieldError';

const NoteForm = ({
  initialNote,
  note,
  noteTypeSlug,
  objectId,
  contentTypeName = 'person',
  withHeading = true,
  successCallback
}: Props) => {
  const { addMessage, currentUser } = useContext(AppContext);
  const noteRepository = NoteRepository();
  const { data: noteTypes, isPending: loadingNoteTypes } = useNoteTypes(
    !noteTypeSlug,
    contentTypeName
  );

  const noteCreationDate = note?.created_at ? new Date(note.created_at) : null;
  const remainingMinutesToUpdate = noteCreationDate ? 5 - minutesBetween(noteCreationDate) : null;
  const [windowTimedOut, setWindowTimedOut] = useState(
    () => !!(remainingMinutesToUpdate && remainingMinutesToUpdate <= 0)
  );

  useTimeout(
    () => {
      setWindowTimedOut(true);
    },
    (remainingMinutesToUpdate ?? 0) * 60 * 1000
  );

  const initialFormValues = useMemo((): NoteFormData => {
    if (note) {
      return {
        id: note.id,
        author_id: currentUser?.id,
        body: note.body,
        note_type_slug: noteTypeSlug
          ? { label: noteTypeSlug, value: noteTypeSlug }
          : note.note_type
            ? { label: note.note_type.title, value: note.note_type.slug }
            : null,
        content_type_name: contentTypeName,
        object_id: note.object_id
      };
    }

    if (objectId) {
      return {
        author_id: currentUser?.id,
        body: initialNote ?? '',
        note_type_slug: noteTypeSlug ? { label: noteTypeSlug, value: noteTypeSlug } : null,
        object_id: objectId,
        content_type_name: contentTypeName
      };
    }

    throw new Error('NoteForm requires either an existing note or an objectId');
  }, [currentUser?.id, initialNote, note, noteTypeSlug, objectId, contentTypeName]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch
  } = useForm({ values: initialFormValues });
  const values = watch();

  const updateNote = useMutation({
    mutationFn: ({ values }: { values: NotePayload }) => {
      return noteRepository.updateNote({ note: values });
    },
    onSuccess: () => {
      addMessage([{ level: 'success', text: 'Successfully updated note' }]);
      successCallback?.();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages((error.response?.data as string) || error));
    }
  });

  const createNote = useMutation({
    mutationFn: ({ values }: { values: NotePayload }) => {
      return noteRepository.createNote({ note: values });
    },
    onSuccess: () => {
      reset();
      addMessage({ level: 'success', text: 'Successfully created note' });
      successCallback?.();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitHandler = () => {
    const payload: NotePayload = {
      ...values,
      body: values.body || null,
      note_type_slug: values.note_type_slug?.value || null
    };

    if (payload.id) {
      updateNote.mutate({ values: payload });
    } else {
      createNote.mutate({ values: payload });
    }
  };

  const isSubmitting = values.id ? updateNote.isPending : createNote.isPending;

  return (
    <>
      {withHeading && <Heading value={3} text={`${note ? 'Edit' : 'Create'} Note`} />}

      <form onSubmit={handleSubmit(submitHandler)}>
        {!noteTypeSlug && (
          <>
            {loadingNoteTypes ? (
              <Spinner label="Loading note types..." />
            ) : noteTypes?.data?.length ? (
              <InputGroup>
                <Controller
                  name="note_type_slug"
                  control={control}
                  rules={{ required: 'Note Type is required' }}
                  render={({ field }) => {
                    return (
                      <AutocompleteSingleSelect
                        label="Note Type *"
                        onChange={field.onChange}
                        options={(noteTypes?.data ?? []).map(({ slug, title, description }) => ({
                          label: title,
                          value: slug,
                          subLabel: description
                        }))}
                        placeholder="Select a Note Type"
                        selectedOption={field.value || emptySelectOption}
                      />
                    );
                  }}
                />
                <FieldError message={errors.note_type_slug?.message} />
              </InputGroup>
            ) : null}
          </>
        )}

        <InputGroup>
          <Label htmlFor="id-body" text="Note Body">
            <Controller
              name="body"
              control={control}
              render={({ field }) => {
                return <RichTextEditor onChange={field.onChange} value={values.body} />;
              }}
            />
          </Label>
        </InputGroup>

        <InputGroup>
          <Button
            disabled={isSubmitting || windowTimedOut}
            type="submit"
            className="w-full px-4 py-2"
            title={`${
              windowTimedOut
                ? 'Cannot update the note after 5 minutes have passed since its creation'
                : `${note?.id ? 'Update' : 'Create'} Note`
            }`}>
            {isSubmitting ? (
              <Spinner color={Color.WHITE} label="Submitting..." />
            ) : (
              `${note?.id ? 'Update' : 'Create'} Note`
            )}
          </Button>
        </InputGroup>
      </form>
    </>
  );
};

export default NoteForm;
